import { useMemo, useRef, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { ItemMilestone } from '@/types/item';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useMilestoneTypes } from '@/hooks/milestones/useMilestoneTypes';
import { logger } from '@/lib/logger';
import { toDateKey } from '@/utils/dateTZ';
import { useAppSettings } from '@/hooks/useAppSettings';
import { hexToRgba, resolveHighlightStyle, HIGHLIGHT_BAR_STYLE_KEY, HIGHLIGHT_BAR_STYLE_DEFAULTS } from '@/utils/highlightStyle';

// Helper function to get local date string
const ymd = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Date-only YYYY-MM-DD add (no new Date(str) on raw key)
const addDaysToDateKey = (key: string, days: number): string => {
  const [y, m, d] = key.split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  dt.setDate(dt.getDate() + days);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

// Days between two YYYY-MM-DD keys (b - a), date-only math
const daysBetweenKeys = (a: string, b: string): number => {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  const da = new Date(ay, (am || 1) - 1, ad || 1);
  const db = new Date(by, (bm || 1) - 1, bd || 1);
  return Math.round((db.getTime() - da.getTime()) / 86400000);
};

// Clamp YYYY-MM-DD key into [min, max] (lexicographic comparison is valid for this format)
const clampDateKey = (k: string, min: string, max: string): string =>
  k < min ? min : k > max ? max : k;

// Format YYYY-MM-DD → dd/MM/yy
const fmtDateKey = (key: string): string => {
  const [y, m, d] = key.split('-');
  return `${d}/${m}/${y.slice(2)}`;
};

const EDGE_PX = 6;
type DragMode = 'move' | 'resize-start' | 'resize-end' | null;

interface MilestoneBarProps {
  milestone: ItemMilestone;
  dateColumns: Date[];
  dayWidth?: number;
  height?: number;
  canDrag?: boolean;
  highlighted?: boolean;
  onCommitDates?: (id: string, itemId: string, start_date: string, end_date: string) => void;
  onRequestDependency?: (milestoneId: string) => void;
  itemStartDate?: string;
  itemEndDate?: string;
}

export default function MilestoneBar({
  milestone,
  dateColumns,
  dayWidth = 20,
  height = 6,
  canDrag,
  highlighted = false,
  onCommitDates,
  onRequestDependency,
  itemStartDate,
  itemEndDate,
}: MilestoneBarProps) {
  const { data: milestoneTypes = [] } = useMilestoneTypes();

  const { getSetting } = useAppSettings();
  const highlightStyle = resolveHighlightStyle(getSetting(HIGHLIGHT_BAR_STYLE_KEY, HIGHLIGHT_BAR_STYLE_DEFAULTS));

  const milestoneTypeColor = useMemo(() => {
    const typeName = milestone.milestone_type || 'default';
    const type = milestoneTypes.find(t => t.name === typeName);
    return type?.color || '#64748b';
  }, [milestone.milestone_type, milestoneTypes]);

  const barPosition = useMemo(() => {
    const startDateKey = toDateKey(milestone.start_date);
    const endDateKey = toDateKey(milestone.end_date);

    let startIndex = dateColumns.findIndex(date => ymd(date) === startDateKey);
    let endIndex = dateColumns.findIndex(date => ymd(date) === endDateKey);

    if (startIndex === -1) {
      const startDateObj = new Date(startDateKey + 'T00:00:00');
      if (startDateObj < dateColumns[0]) {
        startIndex = 0;
      } else {
        logger.debug('MilestoneBar', 'Milestone start after visible range', {
          name: milestone.name, startDate: milestone.start_date, startDateKey
        });
        return null;
      }
    }

    if (endIndex === -1) {
      const endDateObj = new Date(endDateKey + 'T00:00:00');
      if (endDateObj > dateColumns[dateColumns.length - 1]) {
        endIndex = dateColumns.length - 1;
      } else {
        logger.debug('MilestoneBar', 'Milestone end before visible range', {
          name: milestone.name, endDate: milestone.end_date, endDateKey
        });
        return null;
      }
    }

    const left = startIndex * dayWidth;
    const width = Math.max(dayWidth, (endIndex - startIndex + 1) * dayWidth);

    return { left, width };
  }, [milestone.start_date, milestone.end_date, dateColumns, dayWidth]);

  const getBorderStyle = (status: string) => {
    switch (status) {
      case 'pending': return 'border-dashed';
      case 'in_progress':
      case 'completed':
      case 'blocked': return 'border-solid';
      default: return 'border-solid';
    }
  };

  const interactive = !!canDrag && !!onCommitDates && !!milestone.id;
  const [dragMode, setDragMode] = useState<DragMode>(null);
  const [hoverEdge, setHoverEdge] = useState<'start' | 'end' | null>(null);
  const [, setTick] = useState(0);
  const isDragging = dragMode !== null;

  const startXRef = useRef(0);
  const origStartRef = useRef<string>('');
  const origEndRef = useRef<string>('');
  const liveStartRef = useRef<string>('');
  const liveEndRef = useRef<string>('');

  const itemStartKey = itemStartDate ? toDateKey(itemStartDate) : undefined;
  const itemEndKey = itemEndDate ? toDateKey(itemEndDate) : undefined;

  const detectEdge = useCallback((offsetX: number, width: number): DragMode => {
    if (width < 2 * EDGE_PX) {
      return offsetX < width / 2 ? 'resize-start' : 'resize-end';
    }
    if (offsetX <= EDGE_PX) return 'resize-start';
    if (offsetX >= width - EDGE_PX) return 'resize-end';
    return 'move';
  }, []);

  const computeLive = useCallback((mode: DragMode, rawDelta: number): { start: string; end: string } => {
    const origStart = origStartRef.current;
    const origEnd = origEndRef.current;
    if (mode === 'move') {
      const minDelta = itemStartKey ? daysBetweenKeys(origStart, itemStartKey) : -Infinity;
      const maxDelta = itemEndKey ? daysBetweenKeys(origEnd, itemEndKey) : Infinity;
      let d = rawDelta;
      if (minDelta > maxDelta) d = 0;
      else d = Math.min(maxDelta, Math.max(minDelta, rawDelta));
      return { start: addDaysToDateKey(origStart, d), end: addDaysToDateKey(origEnd, d) };
    }
    if (mode === 'resize-start') {
      const minStart = itemStartKey ?? '0000-01-01';
      const maxStart = origEnd;
      const s = clampDateKey(addDaysToDateKey(origStart, rawDelta), minStart, maxStart);
      return { start: s, end: origEnd };
    }
    if (mode === 'resize-end') {
      const minEnd = origStart;
      const maxEnd = itemEndKey ?? '9999-12-31';
      const e = clampDateKey(addDaysToDateKey(origEnd, rawDelta), minEnd, maxEnd);
      return { start: origStart, end: e };
    }
    return { start: origStart, end: origEnd };
  }, [itemStartKey, itemEndKey]);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!interactive || e.button !== 0) return;
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const mode = detectEdge(offsetX, rect.width);
    try { (e.currentTarget as Element).setPointerCapture(e.pointerId); } catch {}
    startXRef.current = e.clientX;
    origStartRef.current = milestone.start_date;
    origEndRef.current = milestone.end_date;
    liveStartRef.current = milestone.start_date;
    liveEndRef.current = milestone.end_date;
    setDragMode(mode);
  }, [interactive, milestone.start_date, milestone.end_date, detectEdge]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!interactive) return;
    if (!isDragging) {
      // hover edge detection
      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const w = rect.width;
      let edge: 'start' | 'end' | null = null;
      if (w < 2 * EDGE_PX) {
        edge = offsetX < w / 2 ? 'start' : 'end';
      } else if (offsetX <= EDGE_PX) edge = 'start';
      else if (offsetX >= w - EDGE_PX) edge = 'end';
      if (edge !== hoverEdge) setHoverEdge(edge);
      return;
    }
    const raw = Math.round((e.clientX - startXRef.current) / dayWidth);
    const live = computeLive(dragMode, raw);
    if (live.start !== liveStartRef.current || live.end !== liveEndRef.current) {
      liveStartRef.current = live.start;
      liveEndRef.current = live.end;
      setTick(t => t + 1);
    }
  }, [interactive, isDragging, dragMode, dayWidth, computeLive, hoverEdge]);

  const handlePointerLeave = useCallback(() => {
    if (!isDragging && hoverEdge !== null) setHoverEdge(null);
  }, [isDragging, hoverEdge]);

  const finishDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    try { (e.currentTarget as Element).releasePointerCapture(e.pointerId); } catch {}
    const mode = dragMode;
    const ls = liveStartRef.current;
    const le = liveEndRef.current;
    const os = origStartRef.current;
    const oe = origEndRef.current;
    setDragMode(null);
    setHoverEdge(null);
    if (!onCommitDates) return;
    if (mode === 'move') {
      if (ls !== os || le !== oe) onCommitDates(milestone.id, milestone.item_id, ls, le);
    } else if (mode === 'resize-start') {
      if (ls !== os) onCommitDates(milestone.id, milestone.item_id, ls, oe);
    } else if (mode === 'resize-end') {
      if (le !== oe) onCommitDates(milestone.id, milestone.item_id, os, le);
    }
  }, [isDragging, dragMode, onCommitDates, milestone.id, milestone.item_id]);

  if (!barPosition) return null;

  // Live geometry based on mode
  let liveLeft = barPosition.left;
  let liveWidth = barPosition.width;
  let liveTransform = 'translateY(-50%)';
  if (isDragging) {
    if (dragMode === 'move') {
      const eff = daysBetweenKeys(origStartRef.current, liveStartRef.current);
      liveTransform = `translate(${eff * dayWidth}px, -50%)`;
    } else if (dragMode === 'resize-start') {
      const eff = daysBetweenKeys(origStartRef.current, liveStartRef.current);
      liveLeft = barPosition.left + eff * dayWidth;
      liveWidth = Math.max(dayWidth, barPosition.width - eff * dayWidth);
    } else if (dragMode === 'resize-end') {
      const eff = daysBetweenKeys(origEndRef.current, liveEndRef.current);
      liveWidth = Math.max(dayWidth, barPosition.width + eff * dayWidth);
    }
  }

  const cursor = !interactive
    ? undefined
    : isDragging
      ? (dragMode === 'move' ? 'grabbing' : 'ew-resize')
      : (hoverEdge ? 'ew-resize' : 'grab');

  const barElement = (
    <div
      className={cn(
        "absolute top-1/2 rounded-sm border-2 overflow-hidden",
        getBorderStyle(milestone.status)
      )}
      style={{
        left: `${isDragging ? liveLeft : barPosition.left}px`,
        width: `${isDragging ? liveWidth : barPosition.width}px`,
        height: `${height}px`,
        backgroundColor: highlighted
          ? hexToRgba(highlightStyle.fill_color, highlightStyle.fill_opacity)
          : `${milestoneTypeColor}33`,
        borderColor: highlighted ? 'transparent' : milestoneTypeColor,
        zIndex: 20,
        transform: isDragging ? liveTransform : 'translateY(-50%)',
        ...(interactive ? { cursor, touchAction: 'none' as const } : {}),
        outline: highlighted
          ? (highlightStyle.border_enabled
              ? `${highlightStyle.border_width}px dashed ${highlightStyle.border_color}`
              : undefined)
          : undefined,
      }}
      onPointerDown={interactive ? handlePointerDown : undefined}
      onPointerMove={interactive ? handlePointerMove : undefined}
      onPointerLeave={interactive ? handlePointerLeave : undefined}
      onPointerUp={interactive ? finishDrag : undefined}
      onPointerCancel={interactive ? finishDrag : undefined}
      onDoubleClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onRequestDependency?.(milestone.id);
      }}
    >
      <div
        className="absolute inset-y-0 left-0 rounded-sm"
        style={{
          width: `${milestone.completion_percentage}%`,
          backgroundColor: highlighted
            ? hexToRgba(highlightStyle.fill_color, Math.min(1, highlightStyle.fill_opacity + 0.3))
            : milestoneTypeColor,
        }}
      />
    </div>
  );

  // Tooltip live dates (mode-aware)
  const ttStart = isDragging ? liveStartRef.current : '';
  const ttEnd = isDragging ? liveEndRef.current : '';
  const tooltipLeft = liveLeft + liveWidth / 2 + (dragMode === 'move'
    ? daysBetweenKeys(origStartRef.current, liveStartRef.current) * dayWidth
    : 0);
  // For move, liveLeft is unchanged so add translate offset; for resize, liveLeft already shifted
  const tooltipCenter = dragMode === 'move'
    ? (barPosition.left + daysBetweenKeys(origStartRef.current, liveStartRef.current) * dayWidth + barPosition.width / 2)
    : (liveLeft + liveWidth / 2);

  const showStartEdge = (hoverEdge === 'start' && !isDragging) || dragMode === 'resize-start';
  const showEndEdge = (hoverEdge === 'end' && !isDragging) || dragMode === 'resize-end';
  const edgeLeftX = dragMode === 'move'
    ? barPosition.left + daysBetweenKeys(origStartRef.current, liveStartRef.current) * dayWidth
    : liveLeft;
  const edgeRightX = edgeLeftX + (dragMode === 'move' ? barPosition.width : liveWidth);

  return (
    <>
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            {barElement}
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            <div className="space-y-1">
              <div className="font-semibold">{milestone.name}</div>
              <div className="text-muted-foreground">
                {milestone.start_date} → {milestone.end_date}
              </div>
              <div className="flex items-center gap-2">
                <span className="capitalize">{milestone.status.replace('_', ' ')}</span>
                <span>•</span>
                <span>{milestone.completion_percentage}% complete</span>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {showStartEdge && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: `${edgeLeftX - 1}px`,
            width: '2px',
            height: `${height + 8}px`,
            transform: 'translateY(-50%)',
            backgroundColor: milestoneTypeColor,
            pointerEvents: 'none',
            zIndex: 30,
          }}
        />
      )}
      {showEndEdge && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: `${edgeRightX - 1}px`,
            width: '2px',
            height: `${height + 8}px`,
            transform: 'translateY(-50%)',
            backgroundColor: milestoneTypeColor,
            pointerEvents: 'none',
            zIndex: 30,
          }}
        />
      )}
      {isDragging && (
        <div
          className="absolute px-2 py-1 rounded text-xs font-medium whitespace-nowrap"
          style={{
            top: '50%',
            left: `${tooltipCenter}px`,
            transform: 'translate(-50%, calc(-50% - 14px))',
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            color: '#f9fafb',
            zIndex: 50,
            pointerEvents: 'none',
          }}
        >
          {fmtDateKey(ttStart)} → {fmtDateKey(ttEnd)}
        </div>
      )}
    </>
  );
}
